import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Box, Button, Stack, TintedStatusPill, Typography } from '../../ui/core';
import { useFormatters } from '../../hooks';
import useCities from '../../hooks/useCities';
import {
  HISTORY_STATUS_LABELS,
  cancelledByLabel,
  formatWindow,
  reasonCell,
  scopeCell,
} from './throttleFormatters';

// Expired rows never got cancelled by anyone — the scope just stopped
// applying at end of the paused service day — so "Ended" shows that date
// instead of a cancelledBy/cancelledAt pair.
const EndedCell = ({ row, f }) => {
  if (row.status === 'EXPIRED') {
    return (
      <Typography sx={{ fontSize: '13px' }}>
        end of day {f.date.toLocaleString(row.serviceDate, f.date.DateFormats.DATE_MED)}
      </Typography>
    );
  }

  return (
    <Stack spacing="2px">
      <Typography sx={{ fontSize: '13px' }}>{cancelledByLabel(row)}</Typography>
      <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
        {f.date.toLocaleString(row.cancelledAt, f.date.DateFormats.DATETIME_MED)}
      </Typography>
    </Stack>
  );
};

const HistoryThrottlesTable = ({ rows, loading, hasMore, loadingMore, onLoadMore }) => {
  const f = useFormatters();
  const { cities } = useCities();
  const cityName = (cityId) => (cities.find((c) => c.id === cityId) || {}).cityName || cityId;

  return (
    <Box>
      {!loading && rows.length === 0 ? (
        <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
          No throttle history yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>City</TableCell>
                <TableCell>Day</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Window</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ended</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{cityName(row.cityId)}</TableCell>
                  <TableCell>{f.date.toLocaleString(row.serviceDate, f.date.DateFormats.DATE_MED)}</TableCell>
                  <TableCell>{scopeCell(row)}</TableCell>
                  <TableCell>{formatWindow(row.windowStart, row.windowEnd)}</TableCell>
                  <TableCell>{reasonCell(row)}</TableCell>
                  <TableCell>
                    <TintedStatusPill palette={null} label={HISTORY_STATUS_LABELS[row.status] || row.status} />
                  </TableCell>
                  <TableCell>
                    <EndedCell row={row} f={f} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {hasMore && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: '12px' }}>
          <Button size="small" variant="outlined" disabled={loadingMore} onClick={onLoadMore}>
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HistoryThrottlesTable;
